import React, { useState, useEffect, useRef } from 'react';

interface AuthGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthed: () => void;
  defaultMode?: 'signin' | 'signup';
  /** Context label used to swap copy. Defaults to 'generate' (anon-prompt
   *  save-your-prompt copy). 'upload' uses upload-specific copy since file
   *  uploads aren't allowed for anon visitors even with the kill switch on. */
  reason?: 'generate' | 'upload';
}

const COPY: Record<NonNullable<AuthGateModalProps['reason']>, {
  signupTitle: string; signupSubtitle: string;
  signinTitle: string; signinSubtitle: string;
  signupSubmit: string; signinSubmit: string;
}> = {
  generate: {
    signupTitle: 'Create your account',
    signinTitle: 'Welcome back',
    signupSubtitle: "We've saved your prompt. Sign up and we'll start building.",
    signinSubtitle: "We've saved your prompt. Sign in to keep going.",
    signupSubmit: 'Create account & generate',
    signinSubmit: 'Sign in & generate',
  },
  upload: {
    signupTitle: 'Sign up to upload',
    signinTitle: 'Sign in to upload',
    signupSubtitle: 'Document upload requires an account. Create one to keep your file and generate from it.',
    signinSubtitle: 'Document upload requires an account. Sign in and your file is ready to generate.',
    signupSubmit: 'Create account & upload',
    signinSubmit: 'Sign in & upload',
  },
};

const AuthGateModal: React.FC<AuthGateModalProps> = ({ isOpen, onClose, onAuthed, defaultMode = 'signup', reason = 'generate' }) => {
  const [mode, setMode] = useState<'signin' | 'signup'>(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setMode(defaultMode);
      setError(null);
      setTimeout(() => emailRef.current?.focus(), 50);
    }
  }, [isOpen, defaultMode]);

  if (!isOpen) return null;

  const handleSignIn = async (): Promise<void> => {
    const body = new URLSearchParams();
    body.set('username', email);
    body.set('password', password);
    const resp = await fetch('/user/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: body.toString(),
    });
    const data = await resp.json().catch(() => null);
    if (!data) throw new Error('Sign-in failed. Please try again.');
    // Login error path returns the Treemo error object directly: { errorCode, errorMessage }
    if (data.errorMessage) throw new Error(data.errorMessage);
    if (data.error && data.error.errorMessage) throw new Error(data.error.errorMessage);
    // Success: server already set the auth cookie. Flag global state for any
    // downstream gate that checks isLoggedIn before re-fetch.
    (window as any).flowboardConfig = { ...(window as any).flowboardConfig, isLoggedIn: true };
  };

  const handleSignUp = async (): Promise<void> => {
    const body = new URLSearchParams();
    body.set('email', email);
    body.set('password', password);
    body.set('display_name', displayName);
    body.set('submitted', '1');
    const resp = await fetch('/user/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: body.toString(),
    });
    const data = await resp.json().catch(() => null);
    if (!data) throw new Error('Sign-up failed. Please try again.');
    if (data.error) throw new Error(typeof data.error === 'string' ? data.error : 'Sign-up failed.');
    if (!data.success) throw new Error('Sign-up failed. Please try again.');
    (window as any).flowboardConfig = { ...(window as any).flowboardConfig, isLoggedIn: true };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password || (mode === 'signup' && !displayName)) {
      setError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'signin') {
        await handleSignIn();
      } else {
        await handleSignUp();
      }
      onAuthed();
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = COPY[reason];
  return (
    <div className="auth-gate-overlay" onClick={onClose}>
      <div className="auth-gate-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-gate-close" onClick={onClose} aria-label="Close">×</button>
        <h2 className="auth-gate-title">
          {mode === 'signup' ? copy.signupTitle : copy.signinTitle}
        </h2>
        <p className="auth-gate-subtitle">
          {mode === 'signup' ? copy.signupSubtitle : copy.signinSubtitle}
        </p>

        <form onSubmit={handleSubmit} className="auth-gate-form">
          {mode === 'signup' && (
            <input
              type="text"
              placeholder="Your name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              required
              disabled={submitting}
            />
          )}
          <input
            ref={emailRef}
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete={mode === 'signup' ? 'email' : 'username'}
            required
            disabled={submitting}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            required
            disabled={submitting}
          />

          {error && <div className="auth-gate-error">{error}</div>}

          <button type="submit" className="auth-gate-submit" disabled={submitting}>
            {submitting
              ? (mode === 'signup' ? 'Creating account…' : 'Signing in…')
              : (mode === 'signup' ? copy.signupSubmit : copy.signinSubmit)}
          </button>
        </form>

        {/* Social sign-in — matches /login + /register pages. Both endpoints
            honor the referring_page cookie set server-side on /create, so
            after callback the user lands back here and the pending submit
            args replay (anon-flow stash is in pendingSubmitRef). */}
        <div className="auth-gate-divider"><span>or</span></div>
        <div className="auth-gate-socials">
          <a className="auth-gate-social" href="/auth/apple" data-track="apple_login">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width="18" height="18">
              <path d="M16.365 1.43c0 1.14-.418 2.144-1.254 3.012-.899.93-1.881 1.466-2.946 1.372-.129-1.096.379-2.258 1.178-3.022.879-.847 2.389-1.495 3.022-1.362ZM20.77 17.11c-.535 1.234-.793 1.786-1.482 2.876-.963 1.48-2.321 3.324-4.002 3.344-1.493.02-1.88-.972-3.91-.962-2.029.01-2.456.992-3.949.972-1.681-.02-2.965-1.678-3.928-3.158-2.69-4.13-2.974-8.976-1.314-11.555 1.176-1.827 3.034-2.896 4.783-2.896 1.78 0 2.9.982 4.37.982 1.425 0 2.296-.982 4.35-.982 1.552 0 3.195.848 4.37 2.312-3.842 2.113-3.217 7.616.712 9.067Z"/>
            </svg>
            <span>Continue with Apple</span>
          </a>
          <a className="auth-gate-social" href="/auth/google" data-track="google_login">
            <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18">
              <path fill="#4285F4" d="M21.6 12.23c0-.78-.07-1.53-.2-2.23H12v4.22h5.38a4.6 4.6 0 0 1-1.99 3.02v2.51h3.23c1.89-1.74 2.98-4.31 2.98-7.52Z"/>
              <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.62-2.43l-3.23-2.51c-.9.6-2.04.95-3.39.95-2.6 0-4.81-1.76-5.6-4.13H3.06v2.59A9.99 9.99 0 0 0 12 22Z"/>
              <path fill="#FBBC05" d="M6.4 13.88A6.02 6.02 0 0 1 6.09 12c0-.65.11-1.28.31-1.88V7.53H3.06A9.99 9.99 0 0 0 2 12c0 1.61.39 3.13 1.06 4.47l3.34-2.59Z"/>
              <path fill="#EA4335" d="M12 5.99c1.47 0 2.79.51 3.83 1.5l2.86-2.86C16.96 3.02 14.7 2 12 2a9.99 9.99 0 0 0-8.94 5.53l3.34 2.59C7.19 7.75 9.4 5.99 12 5.99Z"/>
            </svg>
            <span>Continue with Google</span>
          </a>
        </div>

        <div className="auth-gate-toggle">
          {mode === 'signup' ? (
            <>Already have an account? <button type="button" onClick={() => { setError(null); setMode('signin'); }}>Sign in</button></>
          ) : (
            <>New to Flow2? <button type="button" onClick={() => { setError(null); setMode('signup'); }}>Create an account</button></>
          )}
        </div>

        {mode === 'signin' && (
          <a className="auth-gate-forgot" href="/forgot_password" target="_blank" rel="noreferrer">Forgot password?</a>
        )}
      </div>
    </div>
  );
};

export default AuthGateModal;
